<template>
    <div>
        <div v-if="!modalsOnly" class="version-package-plans" :class="collapsibleCompare ? 'is-modal' : 'is-page'">
        <Spin fix v-if="loading"></Spin>

        <div v-if="isPlanEmpty" class="plan-empty">暂无版本套餐</div>

        <div
            v-if="apiPlanList.length"
            class="plan-cards"
            :class="{ 'is-single': topPlans.length === 1 }"
            :style="{ '--plan-cols': Math.max(topPlans.length, 1) }"
        >
            <div
                v-for="(plan, index) in topPlans"
                :key="'plan-' + (plan.id || index)"
                class="plan-card"
            >
                <div class="plan-card__gradient" :style="{ background: plan.cardGradient }">
                    <div class="plan-card__title-row">
                        <span class="plan-card__name">{{ plan.version_name }}</span>
                        <span v-if="plan.recommended" class="plan-card__badge">推荐</span>
                    </div>
                    <div class="plan-card__gift">{{ getTopGiftText(plan) }}</div>
                </div>
                <div class="plan-card__content">
                    <div class="plan-card__price">
                        <span v-if="showOriginalPrice(plan)" class="plan-card__origin">¥{{ formatPrice(plan.original_price) }}</span>
                        <span class="plan-card__currency">¥</span>
                        <span class="plan-card__amount">{{ formatPrice(getPlanCardAmount(plan)) }}</span>
                        <span class="plan-card__unit">{{ getPriceUnit(plan) }}</span>
                    </div>
                    <button
                        class="plan-card__btn"
                        :style="{ backgroundColor: plan.color }"
                        @click="handleBuy(plan)"
                    >{{ plan.btnText }}</button>
                    <div v-if="getPlanIntro(plan)" class="plan-card__intro">{{ getPlanIntro(plan) }}</div>
                </div>
            </div>
        </div>

        <div v-if="collapsibleCompare && apiPlanList.length" class="intro-modal__toggle" @click="toggleCompare">
            <span>查看详细权益对比</span>
            <Icon :type="compareExpanded ? 'ios-arrow-up' : 'ios-arrow-down'" />
        </div>

        <div
            v-if="apiPlanList.length && (!collapsibleCompare || compareExpanded)"
            class="compare-section"
        >
            <h2 v-if="!collapsibleCompare" class="compare-section__title">{{ compareTitle }}</h2>
            <div
                class="compare-columns"
                :class="{ 'is-single': comparePlans.length === 1 }"
                :style="{ '--plan-cols': Math.max(comparePlans.length, 1) }"
            >
                <div
                    v-for="(plan, planIndex) in comparePlans"
                    :key="'col-' + (plan.id || planIndex)"
                    class="compare-col"
                >
                    <div class="compare-col__header" :style="{ backgroundColor: plan.compareHeaderBg }">
                        <div class="compare-col__name-row">
                            <span class="compare-col__name">{{ plan.version_name }}</span>
                            <span v-if="plan.recommended" class="compare-col__badge">推荐</span>
                        </div>
                        <p v-if="getPlanIntro(plan)" class="compare-col__desc">{{ getPlanIntro(plan) }}</p>
                    </div>
                    <div class="compare-col__body">
                        <div
                            v-for="section in compareSections"
                            :key="section.title + planIndex"
                            class="compare-col__section"
                        >
                            <div class="compare-col__section-title">{{ section.title }}</div>
                            <div
                                v-for="(item, rowIndex) in section.items"
                                :key="section.title + (item.key || item.label || item.name) + rowIndex"
                                class="compare-col__row"
                                :class="{ 'is-alt': rowIndex % 2 === 1 }"
                            >
                                <span class="compare-col__label">{{ item.label || item.name }}</span>
                                <span class="compare-col__value">
                                    <template v-if="section.type === 'tech'">
                                        <i v-if="getTechValue(plan, planIndex, item) === 'check'" class="icon-check"></i>
                                        <span v-else-if="getTechValue(plan, planIndex, item) === 'dash'" class="icon-dash">—</span>
                                        <span v-else class="value-text">{{ getTechValue(plan, planIndex, item) }}</span>
                                    </template>
                                    <template v-else-if="section.type === 'basic'">
                                        <i v-if="hasBasicFeature(plan, item.name)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else-if="section.type === 'plugin'">
                                        <i v-if="hasPluginFeature(plan, item)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else-if="section.type === 'extra'">
                                        <i v-if="hasExtraFeature(plan, item.name)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else>
                                        <i v-if="hasCheckFeature(planIndex, item)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>

        <Modal
            v-model="buyDetailVisible"
            :mask-closable="false"
            footer-hide
            width="960"
            class-name="version-buy-modal"
        >
            <div class="buy-modal" v-if="selectedPlan">
                <div class="buy-modal__top">
                    <span class="buy-modal__title">{{ buyModalTitle }}</span>
                    <Icon type="ios-close" class="buy-modal__close" @click="closeBuyDetail" />
                </div>
                <div class="buy-modal__head">
                    <div class="buy-modal__info">
                        <div class="buy-modal__name-row">
                            <span class="buy-modal__name">{{ selectedPlan.version_name }}</span>
                            <span v-if="renewTimeText" class="buy-modal__renew-time">{{ renewTimeText }}</span>
                        </div>
                        <div v-if="getPlanIntro(selectedPlan)" class="buy-modal__desc">{{ getPlanIntro(selectedPlan) }}</div>
                    </div>
                    <button class="buy-modal__price-btn" type="button" @click="confirmBuy">
                        <Icon type="ios-cart-outline" class="buy-modal__cart" />
                        <span class="buy-modal__price">¥{{ formatPrice(buyModalPayPrice) }}</span>
                        <span class="buy-modal__unit">{{ getPriceUnit(selectedPlan) }}</span>
                    </button>
                </div>
                <div class="buy-modal__columns">
                    <div
                        v-for="section in compareSections"
                        :key="'buy-' + section.title"
                        class="buy-modal__col"
                    >
                        <div class="buy-modal__col-head">{{ section.title }}</div>
                        <div class="buy-modal__col-body">
                            <div
                                v-for="(item, rowIndex) in section.items"
                                :key="section.title + (item.key || item.label || item.name) + rowIndex"
                                class="buy-modal__row"
                                :class="{ 'is-alt': rowIndex % 2 === 1 }"
                            >
                                <span class="buy-modal__label">{{ item.label || item.name }}</span>
                                <span class="buy-modal__value">
                                    <template v-if="section.type === 'tech'">
                                        <i v-if="getTechValue(selectedPlan, buyPlanIndex, item) === 'check'" class="icon-check"></i>
                                        <span v-else-if="getTechValue(selectedPlan, buyPlanIndex, item) === 'dash'" class="icon-dash">—</span>
                                        <span v-else class="value-text">{{ getTechValue(selectedPlan, buyPlanIndex, item) }}</span>
                                    </template>
                                    <template v-else-if="section.type === 'basic'">
                                        <i v-if="hasBasicFeature(selectedPlan, item.name)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else-if="section.type === 'plugin'">
                                        <i v-if="hasPluginFeature(selectedPlan, item)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else-if="section.type === 'extra'">
                                        <i v-if="hasExtraFeature(selectedPlan, item.name)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                    <template v-else>
                                        <i v-if="hasCheckFeature(buyPlanIndex, item)" class="icon-check"></i>
                                        <span v-else class="icon-dash">—</span>
                                    </template>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>

        <version-package-pay-modal ref="versionPay" @paid="$emit('paid')" />
    </div>
</template>

<script>
import { get_version_package, get_userinfo } from '@/api/versionPackage'
import { getRenewTimeRange, resolveVersionExpireTime } from '@/pages/versionPackage/accountCard'
import { PLAN_THEMES, getCompareTitle } from '../config'
import VersionPackagePayModal from './PayModal'

export default {
    name: 'VersionPackagePlans',
    components: { VersionPackagePayModal },
    props: {
        /** true=续费弹窗（点击展开对比），false=套餐页（直接展示对比） */
        collapsibleCompare: {
            type: Boolean,
            default: false
        },
        active: {
            type: Boolean,
            default: true
        },
        /** 仅挂载购买/支付弹窗（首页续费用） */
        modalsOnly: {
            type: Boolean,
            default: false
        },
        /** 外部传入的用户版本信息（首页升级弹窗） */
        versionUserInfo: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            planLoaded: false,
            apiPlanList: [],
            compareExpanded: false,
            buyDetailVisible: false,
            selectedPlan: null,
            buyMode: 'upgrade',
            renewExpireTime: null,
            localVersionUserInfo: null
        }
    },
    computed: {
        userInfo() {
            return this.$store.state.userInfo
        },
        currentVersionUserInfo() {
            const fromProp = this.versionUserInfo
            if (fromProp && fromProp.version_id != null && fromProp.version_id !== '') {
                return fromProp
            }
            return this.localVersionUserInfo || {}
        },
        isCA() {
            return Number(this.$store.state.identity) === 2
        },
        compareTitle() {
            return getCompareTitle(this.isCA)
        },
        isPlanEmpty() {
            return this.planLoaded && !this.apiPlanList.length
        },
        topPlans() {
            return this.formatApiPlans()
        },
        comparePlans() {
            return this.formatApiPlans()
        },
        compareSections() {
            if (!this.apiPlanList.length) {
                return []
            }
            const plans = this.apiPlanList
            return [
                { title: '技术支持', type: 'tech', items: this.getApiTechItems() },
                { title: '基础功能', type: 'basic', items: this.getUnionBasicFeatures(plans) },
                { title: '插件功能单独购买', type: 'plugin', items: this.getUnionPluginFeatures(plans) },
                { title: '售后服务', type: 'extra', items: this.getUnionExtraFeatures(plans) }
            ].filter(section => section.items && section.items.length > 0)
        },
        buyPlanIndex() {
            if (!this.selectedPlan) return 0
            const idx = this.comparePlans.findIndex(plan => plan.id === this.selectedPlan.id)
            return idx >= 0 ? idx : 0
        },
        buyModalTitle() {
            return this.buyMode === 'renew' ? '版本续费' : '版本购买'
        },
        buyModalPayPrice() {
            if (!this.selectedPlan) return 0
            if (this.buyMode === 'renew') return this.selectedPlan.renewal_fee
            return this.selectedPlan.price
        },
        renewTimeText() {
            if (this.buyMode !== 'renew' || !this.selectedPlan || this.renewExpireTime == null || this.renewExpireTime === '') {
                return ''
            }
            return getRenewTimeRange(this.renewExpireTime, this.selectedPlan.year_num)
        }
    },
    watch: {
        active(val) {
            if (val) {
                this.loadVersionUserInfo()
                if (!this.apiPlanList.length && !this.loading) {
                    this.loadPlanList()
                }
            }
        },
        versionUserInfo: {
            deep: true,
            handler(val) {
                if (val && val.version_id != null) {
                    this.localVersionUserInfo = { ...val }
                }
            }
        }
    },
    created() {
        this.loadVersionUserInfo()
        if (this.active) {
            this.loadPlanList()
        }
    },
    activated() {
        if (!this.collapsibleCompare && this.active && !this.loading) {
            this.loadPlanList()
        }
    },
    methods: {
        resetCompare() {
            this.compareExpanded = false
            this.$emit('compare-change', false)
        },
        toggleCompare() {
            this.compareExpanded = !this.compareExpanded
            this.$emit('compare-change', this.compareExpanded)
        },
        handleBuy(plan) {
            if (this.isCurrentUserPlan(plan)) {
                this.openRenewPlan(plan)
                return
            }
            this.buyMode = 'upgrade'
            this.renewExpireTime = null
            this.selectedPlan = plan
            this.buyDetailVisible = true
        },
        isCurrentUserPlan(plan) {
            if (!plan) return false
            const versionId = this.currentVersionUserInfo.version_id
            if (versionId == null || versionId === '') return false
            return Number(plan.permissions_id) === Number(versionId)
        },
        async ensureVersionUserInfo() {
            if (resolveVersionExpireTime(this.currentVersionUserInfo)) {
                return this.currentVersionUserInfo
            }
            try {
                const res = await get_userinfo()
                this.localVersionUserInfo = res.data || {}
            } catch (e) {
                this.localVersionUserInfo = {}
            }
            return this.currentVersionUserInfo
        },
        async openRenewPlan(plan) {
            await this.ensureVersionUserInfo()
            this.buyMode = 'renew'
            this.renewExpireTime = resolveVersionExpireTime(this.currentVersionUserInfo)
            this.selectedPlan = plan
            this.buyDetailVisible = true
        },
        loadVersionUserInfo() {
            if (this.versionUserInfo && this.versionUserInfo.version_id != null) {
                this.localVersionUserInfo = { ...this.versionUserInfo }
                return Promise.resolve(this.localVersionUserInfo)
            }
            return get_userinfo().then(res => {
                this.localVersionUserInfo = res.data || {}
                return this.localVersionUserInfo
            }).catch(() => {
                this.localVersionUserInfo = {}
                return this.localVersionUserInfo
            })
        },
        findPlanByVersionId(versionId) {
            return this.formatApiPlans().find(
                plan => Number(plan.permissions_id) === Number(versionId)
            ) || null
        },
        async openRenewByVersionId(userInfo = {}) {
            let info = typeof userInfo === 'object' && userInfo !== null
                ? { ...userInfo }
                : { version_id: userInfo }
            if (info.version_id == null || info.version_id === '') {
                this.$Message.warning('无法获取当前版本信息')
                return
            }
            if (!resolveVersionExpireTime(info)) {
                try {
                    const res = await get_userinfo()
                    info = { ...info, ...(res.data || {}) }
                } catch (e) {
                    // ignore
                }
            }
            if (!this.apiPlanList.length) {
                await this.loadPlanList()
            }
            const plan = this.findPlanByVersionId(info.version_id)
            if (!plan) {
                this.$Message.warning('未找到对应版本套餐')
                return
            }
            this.localVersionUserInfo = { ...info }
            await this.openRenewPlan(plan)
        },
        closeBuyDetail() {
            this.buyDetailVisible = false
            this.selectedPlan = null
            this.buyMode = 'upgrade'
            this.renewExpireTime = null
        },
        confirmBuy() {
            const plan = this.selectedPlan
            if (!plan) return
            this.buyDetailVisible = false
            this.$refs.versionPay.open(plan, { isRenewal: this.buyMode === 'renew' })
        },
        dedupeBasicFeatures(list = []) {
            const map = {}
            list.forEach(item => {
                const name = item && item.feature_name
                if (!name) return
                map[name] = item
            })
            return Object.values(map)
        },
        splitPluginFeatures(list = []) {
            const catalog = {}
            const included = {}
            list.forEach(item => {
                const key = item.permission_id || item.feature_name
                if (!key || !item.feature_name) return
                const price = parseFloat(item.price) || 0
                if (price > 0) {
                    if (!catalog[key] || price > (parseFloat(catalog[key].price) || 0)) {
                        catalog[key] = item
                    }
                    return
                }
                included[key] = item
            })
            return { catalog, included }
        },
        getPluginRowKey(item = {}) {
            if (item.permission_id != null) return String(item.permission_id)
            return item.feature_name || ''
        },
        findPluginFeatureItem(plan, row = {}) {
            return (plan.plugin_features || []).find(item => {
                if (row.permission_id != null) {
                    return Number(item.permission_id) === Number(row.permission_id)
                }
                return item.feature_name === row.feature_name
            }) || null
        },
        hasPluginFeature(plan, row) {
            const found = this.findPluginFeatureItem(plan, row)
            return !!found && Number(found.is_supported) === 1
        },
        getApiTechItems() {
            if (this.isCA) {
                return [
                    { label: '售价', type: 'price' },
                    { label: '原价', type: 'original_price' },
                    { label: '系统授权', type: 'auth' },
                    { label: '赠送合同/短信', type: 'gift_mixed' },
                    { label: '是否支持更新', type: 'update' },
                    { label: '次年费用', type: 'renewal' }
                ]
            }
            return [
                { label: '售价', type: 'price' },
                { label: '原价', type: 'original_price' },
                { label: '系统授权', type: 'auth' },
                { label: '赠送短信', type: 'sms' },
                { label: '是否支持更新', type: 'update' },
                { label: '次年费用', type: 'renewal' }
            ]
        },
        parseExtraFeatures(extra) {
            if (extra == null || extra === '') return []
            if (Array.isArray(extra)) {
                return extra.map(item => String(item).trim()).filter(Boolean)
            }
            return String(extra)
                .split(/[,，]/)
                .map(item => item.trim())
                .filter(Boolean)
        },
        getPlanExtraFeatures(plan) {
            if (!plan) return []
            if (Array.isArray(plan.extra_feature_list) && plan.extra_feature_list.length) {
                return plan.extra_feature_list
            }
            return this.parseExtraFeatures(plan.extra_features)
        },
        normalizePlanItem(item) {
            const { catalog, included } = this.splitPluginFeatures(item.plugin_features || [])
            return {
                ...item,
                description: item.description || '',
                is_permanent: Number(item.is_permanent),
                support_update: item.support_update != null ? Number(item.support_update) : 1,
                basic_features: this.dedupeBasicFeatures(item.basic_features || []),
                plugin_features: Array.isArray(item.plugin_features) ? item.plugin_features : [],
                plugin_catalog: catalog,
                plugin_included: included,
                extra_features: item.extra_features || '',
                extra_feature_list: this.parseExtraFeatures(item.extra_features)
            }
        },
        getPlanIntro(plan) {
            return plan.description || plan.introduction || ''
        },
        formatPrice(value) {
            if (value == null || value === '') return '0'
            const num = Number(value)
            if (Number.isNaN(num)) return String(value)
            if (num === 0) return '0'
            const fixed = Number.isInteger(num) ? String(num) : num.toFixed(2)
            return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
        },
        hasPriceValue(value) {
            return value !== null && value !== undefined && value !== ''
        },
        showOriginalPrice(plan) {
            if (!this.hasPriceValue(plan.original_price)) return false
            const origin = Number(plan.original_price)
            const price = Number(plan.price)
            if (Number.isNaN(origin)) return false
            if (Number.isNaN(price)) return origin > 0
            return origin > price
        },
        getUnionBasicFeatures(plans) {
            const names = []
            const seen = new Set()
            const extraNames = new Set()
            const pluginNames = new Set()
            plans.forEach(plan => {
                this.getPlanExtraFeatures(plan).forEach(name => extraNames.add(name))
                Object.values(plan.plugin_catalog || {}).forEach(item => pluginNames.add(item.feature_name))
                Object.values(plan.plugin_included || {}).forEach(item => pluginNames.add(item.feature_name))
            })
            plans.forEach(plan => {
                this.dedupeBasicFeatures(plan.basic_features || []).forEach(item => {
                    const featureName = item.feature_name
                    if (!featureName || seen.has(featureName)) return
                    if (extraNames.has(featureName) || pluginNames.has(featureName)) return
                    seen.add(featureName)
                    names.push({ name: featureName })
                })
            })
            return names
        },
        getBasicFeatureItem(plan, featureName) {
            return (plan.basic_features || []).find(item => item.feature_name === featureName) || null
        },
        getUnionPluginFeatures(plans) {
            const map = {}
            const ordered = []
            plans.forEach(plan => {
                (plan.plugin_features || []).forEach(item => {
                    const key = this.getPluginRowKey(item)
                    if (!key || map[key]) return
                    const row = {
                        key,
                        permission_id: item.permission_id,
                        feature_name: item.feature_name,
                        price: item.price,
                        name: item.feature_name,
                        label: `${item.feature_name}（${this.formatPrice(item.price)}元）`
                    }
                    map[key] = row
                    ordered.push(row)
                })
            })
            return ordered
        },
        getUnionExtraFeatures(plans) {
            const seen = new Set()
            const items = []
            plans.forEach(plan => {
                this.getPlanExtraFeatures(plan).forEach(name => {
                    if (!seen.has(name)) {
                        seen.add(name)
                        items.push({ name, label: name })
                    }
                })
            })
            return items
        },
        hasBasicFeature(plan, featureName) {
            const found = this.getBasicFeatureItem(plan, featureName)
            return !!found && Number(found.is_supported) === 1
        },
        hasExtraFeature(plan, featureName) {
            return this.getPlanExtraFeatures(plan).includes(featureName)
        },
        getPriceUnit(plan) {
            if (Number(plan.is_permanent) === 1) return '/终身'
            if (plan.year_num) return `/${plan.year_num}年`
            return '/终身'
        },
        getPlanCardAmount(plan) {
            if (plan && plan.isCurrentPlan) {
                return plan.renewal_fee != null && plan.renewal_fee !== '' ? plan.renewal_fee : plan.price
            }
            return plan.price
        },
        formatApiPlans() {
            if (!this.apiPlanList.length) return []
            return this.apiPlanList.map((plan, index) => {
                const theme = PLAN_THEMES[index % PLAN_THEMES.length]
                const isCurrentPlan = this.isCurrentUserPlan(plan)
                return {
                    ...theme,
                    ...plan,
                    tier: index,
                    recommended: plan.recommended != null ? !!plan.recommended : index === 2,
                    isCurrentPlan,
                    btnText: isCurrentPlan ? '立即续费' : '立即购买',
                    introduction: plan.description || ''
                }
            })
        },
        loadPlanList() {
            this.loading = true
            return get_version_package().then(res => {
                const list = Array.isArray(res && res.data) ? res.data : []
                this.apiPlanList = list
                    .map(item => this.normalizePlanItem(item))
                    .sort((a, b) => Number(a.permissions_id || 0) - Number(b.permissions_id || 0))
            }).catch(() => {
                this.apiPlanList = []
            }).finally(() => {
                this.planLoaded = true
                this.loading = false
                if (!this.apiPlanList.length) {
                    this.compareExpanded = false
                    this.$emit('compare-change', false)
                }
            })
        },
        getTopGiftText(plan) {
            if (this.isCA) {
                return `赠送合同：${plan.gift_contract || 0}份/年`
            }
            return `赠送短信：${plan.gift_sms || 0}条`
        },
        getTechValue(plan, planIndex, item) {
            switch (item.type) {
                case 'price':
                    return this.hasPriceValue(plan.price) ? `${this.formatPrice(plan.price)}元` : '—'
                case 'original_price':
                    return this.hasPriceValue(plan.original_price) ? `${this.formatPrice(plan.original_price)}元` : '—'
                case 'auth':
                    return Number(plan.is_permanent) === 1 ? '终身' : `${plan.year_num || 1}年`
                case 'text':
                    return item.value
                case 'sms':
                    return `${plan.gift_sms || 0}条`
                case 'contract':
                    return `${plan.gift_contract || 0}份/年`
                case 'gift_mixed':
                    if (plan.gift_contract) return `${plan.gift_contract}份/年`
                    if (plan.gift_sms) return `${plan.gift_sms}条`
                    return '—'
                case 'update':
                    return Number(plan.support_update) === 1 ? '支持' : '不支持'
                case 'renewal': {
                    const renewalFee = Number(plan.renewal_fee)
                    if (!plan.renewal_fee || Number.isNaN(renewalFee) || renewalFee <= 0) return '—'
                    return `${this.formatPrice(plan.renewal_fee)}元/年`
                }
                case 'brand_price':
                    if (planIndex === 0 || !plan.brand_price) return '—'
                    return `${plan.brand_price}元/单`
                case 'check':
                    if (this.apiPlanList.length && item.label === '自有品牌对接') {
                        return this.hasBasicFeature(plan, '自有品牌对接') ? 'check' : 'dash'
                    }
                    return planIndex >= (item.minTier || 0) ? 'check' : 'dash'
                default:
                    return '—'
            }
        },
        hasCheckFeature(planIndex, feature) {
            return planIndex >= (feature.minTier || 0)
        }
    }
}
</script>

<style scoped lang="scss">
.version-package-plans {
    position: relative;
    min-height: 120px;
}

.plan-empty {
    padding: 48px 16px;
    text-align: center;
    color: #8a94a6;
    font-size: 14px;
    line-height: 1.6;
}

.intro-modal__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin: 20px 0 8px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    user-select: none;

    &:hover {
        color: #4877e8;
    }
}

$single-plan-width: 280px;

.plan-cards {
    display: grid;
    grid-template-columns: repeat(var(--plan-cols, 4), minmax(0, 1fr));
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto 36px;

    &.is-single {
        grid-template-columns: minmax(0, $single-plan-width);
        justify-content: center;
    }
}

.version-package-plans.is-page {
    .plan-cards {
        margin-bottom: 8px;
    }

    .compare-section {
        margin-top: 24px;
        padding-top: 0;
    }
}

.version-package-plans.is-modal {
    $single-plan-width: 220px;

    .plan-cards {
        gap: 16px;
        max-width: none;
        margin: 0 0 16px;
    }

    .compare-columns {
        gap: 16px;
    }
}

.compare-columns {
    display: grid;
    grid-template-columns: repeat(var(--plan-cols, 4), minmax(0, 1fr));
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;

    &.is-single {
        grid-template-columns: minmax(0, $single-plan-width);
        justify-content: center;
    }
}

.plan-card {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(40, 57, 90, 0.05);
    border: 1px solid #edf1f8;
    display: flex;
    flex-direction: column;
}

.plan-card__gradient {
    padding: 16px 14px 18px;
    flex-shrink: 0;
}

.plan-card__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}

.plan-card__name {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
}

.plan-card__badge {
    display: inline-block;
    padding: 1px 7px;
    font-size: 11px;
    line-height: 17px;
    color: #fff;
    background: #ff9f43;
    border-radius: 3px;
}

.plan-card__gift {
    font-size: 12px;
    color: #999;
}

.plan-card__content {
    flex: 1;
    padding: 0 14px 16px;
    display: flex;
    flex-direction: column;
}

.plan-card__price {
    text-align: center;
    margin: 4px 0 12px;
    display: flex;
    align-items: baseline;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
}

.plan-card__origin {
    font-size: 13px;
    color: #999;
    text-decoration: line-through;
}

.plan-card__currency {
    font-size: 14px;
    font-weight: 600;
}

.plan-card__amount {
    font-size: 28px;
    font-weight: 700;
}

.plan-card__unit {
    font-size: 12px;
    color: #999;
}

.plan-card__btn {
    display: block;
    width: 100%;
    height: 36px;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.plan-card__intro {
    margin-top: 10px;
    font-size: 11px;
    color: #888;
    line-height: 1.55;
    text-align: left;
}

.compare-section {
    padding-top: 4px;
    margin-top: 8px;
}

.compare-section__title {
    font-size: 18px;
    font-weight: 700;
    color: #2f3a4f;
    text-align: center;
    margin: 0 0 20px;
    letter-spacing: 1px;
}

.compare-col {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(40, 57, 90, 0.05);
    border: 1px solid #edf1f8;
}

.compare-col__header {
    padding: 14px 10px;
    text-align: center;
    min-height: 76px;
    box-sizing: border-box;
}

.compare-col__name-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}

.compare-col__name {
    font-size: 14px;
    font-weight: 700;
    color: #1a1a1a;
}

.compare-col__badge {
    display: inline-block;
    padding: 1px 7px;
    font-size: 11px;
    color: #fff;
    background: #ff9f43;
    border-radius: 3px;
}

.compare-col__desc {
    font-size: 11px;
    color: #888;
    line-height: 1.5;
    margin: 0;
    min-height: 32px;
}

.compare-col__section-title {
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 700;
    color: #333;
    background: #f5f6f8;
    border-top: 1px solid #eee;
    text-align: center;
}

.compare-col__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    min-height: 32px;
    box-sizing: border-box;

    &.is-alt {
        background: #fafafa;
    }
}

.compare-col__label {
    color: #666;
    font-size: 11px;
    flex: 1;
    padding-right: 6px;
    line-height: 1.4;
}

.compare-col__value {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 18px;
}

.value-text {
    color: #4a90e2;
    font-weight: 500;
    font-size: 11px;
    white-space: nowrap;
}

.icon-check {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #28c76f;
    position: relative;
    flex-shrink: 0;

    &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 2px;
        width: 4px;
        height: 7px;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
    }
}

.icon-dash {
    color: #ccc;
    font-size: 13px; 
    width: 14px;
    height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.buy-modal {
    padding: 0 4px 8px;
}

.buy-modal__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.buy-modal__title {
    font-size: 18px;
    font-weight: 600;
    color: #2f3a4f;
}

.buy-modal__close {
    font-size: 28px;
    color: #999;
    cursor: pointer;
}

.buy-modal__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 20px;
}

.buy-modal__name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
}

.buy-modal__name {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
}

.buy-modal__renew-time {
    font-size: 14px;
    font-weight: 500;
    color: #ff643d;
    line-height: 1.4;
}

.buy-modal__desc {
    font-size: 13px;
    color: #666;
    line-height: 1.6;
}

.buy-modal__price-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: 1px solid #5ba3f5;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
}

.buy-modal__cart {
    font-size: 22px;
    color: #fe511c;
}

.buy-modal__price {
    font-size: 20px;
    font-weight: 600;
    color: #fe511c;
}

.buy-modal__unit {
    font-size: 14px;
    color: #5ba3f5;
}

.buy-modal__columns {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid #e5e9f2;
    border-radius: 4px;
    overflow: hidden;
}

.buy-modal__col {
    border-right: 1px solid #e5e9f2;

    &:last-child {
        border-right: none;
    }
}

.buy-modal__col-head {
    padding: 10px 8px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    background: #d9ecff;
    border-bottom: 1px solid #e5e9f2;
}

.buy-modal__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    min-height: 34px;
    gap: 6px;

    &.is-alt {
        background: #fafafa;
    }
}

.buy-modal__label {
    flex: 1;
    font-size: 12px;
    color: #666;
}

::v-deep .version-buy-modal {
    .ivu-modal-body {
        padding: 20px 24px 24px;
    }

    .ivu-modal-close {
        display: none;
    }
}
</style>
